<template>
  <div class="script-errors-page animated fadeIn">
    <b-row>
      <b-col cols="12">
        <b-card no-body class="shadow-sm">
          <b-card-header class="d-flex flex-wrap justify-content-between align-items-center py-2">
            <div class="mr-3">
              <i class="fa fa-bug text-warning"></i> <strong>Script Errors</strong>
              <span v-if="loading" class="script-errors-loading ml-2">
                <i class="fa fa-circle-o-notch fa-spin mr-1"></i> Loading...
              </span>
              <div class="small text-muted">
                Last refresh: {{ lastRefreshLabel }} - Showing up to 100 grouped errors.
              </div>
            </div>

            <div class="d-flex flex-wrap align-items-center">
              <servermenu v-model="activeServer" :show-all="false" class="mr-2 mb-2 mb-md-0"></servermenu>

              <b-input-group size="sm" class="script-errors-search mr-2 mb-2 mb-md-0">
                <b-input-group-prepend is-text class="bg-dark border-secondary">
                  <i class="fa fa-search small"></i>
                </b-input-group-prepend>
                <b-form-input
                  v-model.trim="search"
                  placeholder="Search NPCID, Username, Traceback..."
                  class="bg-dark text-white border-secondary"
                ></b-form-input>
                <b-input-group-append v-if="search">
                  <b-button variant="outline-secondary" class="border-secondary py-0" @click="clearSearch">
                    <i class="fa fa-times small"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>

              <b-button size="sm" variant="outline-info" :disabled="loading" @click="refreshScriptErrors">
                <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': loading }"></i> Refresh
              </b-button>
            </div>
          </b-card-header>

          <b-overlay :show="loading && rows.length === 0" variant="transparent" opacity="0.65" blur="1px" rounded="sm">
            <div class="table-responsive">
              <table class="table table-dark table-hover mb-0 script-errors-table">
                <colgroup>
                  <col class="col-id">
                  <col class="col-npcid">
                  <col class="col-count">
                  <col class="col-usernames">
                  <col>
                </colgroup>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>NPCID</th>
                    <th>Count</th>
                    <th>Username</th>
                    <th>Traceback</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rows" :key="row.id + '-' + row.npcid" @contextmenu.prevent="openContextMenu($event, row)">
                    <td class="script-errors-id text-monospace">{{ row.id }}</td>
                    <td class="script-errors-npcid text-monospace">{{ row.npcid }}</td>
                    <td>
                      <span class="badge badge-info px-2">{{ row.count }}</span>
                    </td>
                    <td class="script-errors-usernames">
                      <div class="script-errors-username-list">
                        <span v-for="(username, index) in displayUsernames(row.username)" :key="username + '-' + index">
                          <span v-if="index > 0" class="script-errors-comma">, </span><router-link :to="'/user/' + username + '/profile'" class="script-errors-username-link">{{ username }}</router-link>
                        </span>
                      </div>
                    </td>
                    <td>
                      <pre class="script-errors-traceback">{{ row.traceback }}</pre>
                    </td>
                  </tr>
                  <tr v-if="rows.length === 0">
                    <td colspan="5" class="text-center text-muted py-5">
                      No script errors found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-overlay>
        </b-card>
      </b-col>
    </b-row>
    <div
      v-if="contextMenu.visible"
      class="script-errors-context-menu"
      :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      @click.stop>
      <button type="button" class="script-errors-context-item" @click="copyNteleCommand">
        <i class="fa fa-copy mr-2"></i> Copy /ntele Command
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import Servermenu from '@/views/dashboard/Servermenu'

const REFRESH_INTERVAL = 5 * 60 * 1000
const MAX_DISPLAY_USERNAMES = 10

export default {
  name: 'ScriptErrors',
  components: {
    Servermenu
  },
  data () {
    return {
      activeServer: 'gold',
      search: '',
      loading: false,
      refreshTimer: null,
      pendingServer: '',
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        row: null
      }
    }
  },
  computed: {
    ...mapGetters({
      scriptErrorsByServer: 'scriptErrorsByServer',
      scriptErrorsUpdatedAtByServer: 'scriptErrorsUpdatedAtByServer'
    }),
    normalizedSearch () {
      return (this.search || '').trim()
    },
    allRows () {
      return this.scriptErrorsByServer[this.activeServer] || []
    },
    rows () {
      const search = this.normalizedSearch.toLowerCase()
      if (!search) return this.allRows
      return this.allRows.filter(row => {
        const npcid = String(row.npcid || '').toLowerCase()
        const usernames = String(row.username || '').toLowerCase()
        const traceback = String(row.traceback || '').toLowerCase()
        return npcid.includes(search) || usernames.includes(search) || traceback.includes(search)
      })
    },
    updatedAt () {
      return this.scriptErrorsUpdatedAtByServer[this.activeServer] || null
    },
    lastRefreshLabel () {
      if (!this.updatedAt) return 'n/a'
      return moment(this.updatedAt).format('YYYY-MM-DD HH:mm:ss')
    }
  },
  watch: {
    activeServer () {
      this.refreshScriptErrors()
    },
    updatedAt () {
      this.finishLoadingIfCurrent()
    }
  },
  created () {
    this.refreshScriptErrors()
  },
  mounted () {
    this.refreshTimer = window.setInterval(() => this.refreshScriptErrors(), REFRESH_INTERVAL)
    document.addEventListener('click', this.closeContextMenu)
  },
  beforeDestroy () {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer)
    }
    document.removeEventListener('click', this.closeContextMenu)
  },
  methods: {
    refreshScriptErrors () {
      if (!this.activeServer) return
      this.loading = true
      this.pendingServer = this.activeServer
      this.emit('getScriptErrors', {
        server: this.activeServer
      })
    },
    clearSearch () {
      this.search = ''
    },
    finishLoadingIfCurrent () {
      if (this.pendingServer && this.pendingServer === this.activeServer) {
        this.loading = false
        this.pendingServer = ''
      }
    },
    splitUsernames (value) {
      if (!value) return []
      return String(value).split(',').map(item => item.trim()).filter(Boolean)
    },
    displayUsernames (value) {
      return this.splitUsernames(value).slice(0, MAX_DISPLAY_USERNAMES)
    },
    openContextMenu (event, row) {
      this.contextMenu = {
        visible: true,
        x: event.clientX,
        y: event.clientY,
        row
      }
    },
    closeContextMenu () {
      if (!this.contextMenu.visible) return
      this.contextMenu = {
        visible: false,
        x: 0,
        y: 0,
        row: null
      }
    },
    copyNteleCommand () {
      const row = this.contextMenu.row
      if (!row || !row.npcid) return
      this.copyText(`/ntele ${row.npcid}`)
      this.closeContextMenu()
    },
    copyText (text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
        return
      }
      const input = document.createElement('textarea')
      input.value = text
      input.setAttribute('readonly', 'readonly')
      input.style.position = 'fixed'
      input.style.left = '-9999px'
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
    }
  }
}
</script>

<style scoped>
.script-errors-search {
  width: 340px;
}
.script-errors-loading {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  color: #8fd6cc;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.script-errors-table {
  table-layout: fixed;
}
.script-errors-table th {
  border-top: 0;
  font-size: 0.72rem;
  text-transform: uppercase;
  color: #9fb0ba;
}
.script-errors-table td {
  vertical-align: top;
}
.col-id {
  width: 122px;
}
.col-npcid {
  width: 120px;
}
.col-count {
  width: 80px;
}
.col-usernames {
  width: 260px;
}
.script-errors-usernames {
  color: #fff;
  font-size: 0.78rem;
  line-height: 1.45;
  white-space: normal;
  overflow-wrap: anywhere;
}
.script-errors-id,
.script-errors-npcid,
.script-errors-comma {
  color: #fff;
}
.script-errors-id,
.script-errors-npcid {
  font-size: 0.78rem;
  line-height: 1.45;
}
.script-errors-username-list {
  font-size: 0;
  overflow-wrap: anywhere;
}
.script-errors-username-link {
  color: #63c2de;
  font-size: 0.78rem;
  font-weight: 400;
}
.script-errors-comma {
  font-size: 0.78rem;
}
.script-errors-username-link:hover {
  color: #8ad7ec;
}
.script-errors-traceback {
  margin: 0;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.28);
  color: #e5edf2;
  font-size: 0.78rem;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.script-errors-context-menu {
  position: fixed;
  z-index: 2000;
  min-width: 190px;
  background: #20252a;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  padding: 4px 0;
}
.script-errors-context-item {
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
  color: #e5edf2;
  font-size: 0.78rem;
  line-height: 1.4;
  text-align: left;
  padding: 7px 10px;
}
.script-errors-context-item:hover {
  background: rgba(99, 194, 222, 0.14);
  color: #fff;
}
@media (max-width: 991.98px) {
  .script-errors-search {
    width: 100%;
  }
  .col-usernames {
    width: 220px;
  }
}
</style>
